// Bulk-Einladung per CSV (H5, ADR-027): Sachbearbeiter lädt eine Liste
// (Kundennummer + E-Mail) hoch -> fuer jede Zeile eine Einladung + Mail.
// Mehrere Zeilen je Kundennr = mehrere Kontakte pro Mandant.

import { useState } from "react";

import { useBulkInvite } from "@/api/hooks";
import type { BulkInviteResult } from "@/api/types";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";

import "./BulkInviteModal.css";

interface Props {
  onClose: () => void;
}

export function BulkInviteModal({ onClose }: Props) {
  const toast = useToast();
  const bulk = useBulkInvite();
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<BulkInviteResult | null>(null);

  const handleUpload = () => {
    if (!file) return;
    bulk.mutate(file, {
      onSuccess: (res) => {
        setResult(res);
        if (res.created > 0) {
          toast.success(`${res.created} Einladung(en) verschickt.`);
        } else {
          toast.error("Keine Einladung verschickt — siehe Report.");
        }
      },
      onError: (err) => toast.error(`Upload fehlgeschlagen: ${err.message}`),
    });
  };

  return (
    <Modal
      title="Mandanten per CSV einladen"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Schließen
          </Button>
          <Button
            variant="primary"
            onClick={handleUpload}
            loading={bulk.isPending}
            disabled={!file}
          >
            Hochladen &amp; einladen
          </Button>
        </>
      }
    >
      <div className="bmd-subtle bmd-bulk-help">
        CSV mit zwei Spalten: <strong>Kundennummer</strong> und{" "}
        <strong>E-Mail</strong>. Pro Zeile geht eine Einladung raus — eine
        Kundennummer darf mehrfach vorkommen (mehrere Kontakte pro Klient).
        Excel-Export (Semikolon) und Spaltennamen wie „Kundennummer" / „E-Mail"
        werden automatisch erkannt.
      </div>

      <input
        type="file"
        accept=".csv,text/csv"
        aria-label="CSV-Datei"
        onChange={(e) => {
          setFile(e.target.files?.[0] ?? null);
          setResult(null);
        }}
      />

      {result && (
        <div className="bmd-card bmd-bulk-result">
          <div className="bmd-bulk-stat">
            ✓ <strong>{result.created}</strong> Einladung(en) verschickt
            {result.total_rows !== result.created &&
              ` (von ${result.total_rows} Zeilen)`}
            .
          </div>
          {result.not_found.length > 0 && (
            <div className="bmd-bulk-warn">
              {result.not_found.length} unbekannte Kundennummer(n) übersprungen:{" "}
              <span className="bmd-mono">{result.not_found.join(", ")}</span>
            </div>
          )}
          {result.errors.length > 0 && (
            <div className="bmd-bulk-warn">
              {result.errors.length} fehlerhafte Zeile(n):
              <ul className="bmd-bulk-errors">
                {result.errors.map((e) => (
                  <li key={e.row_number}>
                    Zeile {e.row_number}: {e.reason}
                    {e.email && ` — ${e.email}`}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
